import { ReactElement } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import BigNumber from 'bignumber.js';

import { GuardButton } from 'modules/auth/components/GuardButton';
import { Summary } from 'modules/common/components/Summary';
import { SummaryItem } from 'modules/common/components/SummaryItem';
import { ONE } from 'modules/common/const';
import { useGlobalTranslation } from 'modules/i18n';
import { NumberInputs } from 'modules/liquidStaking/components/TabsDialog/components/NumberInputs/NumberInputs';
import { UnstakeTypeSelect } from 'modules/liquidStaking/components/UnstakeTypeSelect/UnstakeTypeSelect';
import { UnstakeType } from 'modules/liquidStaking/const';

import { useUnstakeForm } from '../../../../hooks/useUnstakeForm';
import { translation } from './translation';
import { useStyles } from './useStyles';

export interface IUnstakeFormProps {
  type: UnstakeType;
  setType: (type: UnstakeType) => void;
  onSubmit: (params: { amount: BigNumber; receiveAmount: BigNumber }) => void;
  isSubmitLoading: boolean;
}

export function UnstakeForm({
  type,
  setType,
  onSubmit,
  isSubmitLoading,
}: IUnstakeFormProps): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const {
    balance,
    receiveAmount,
    receiveForOneAmount,
    control,
    isError,
    errorMessage,
    onMaxClick,
    onSubmit: handleSubmit,
    isSubmitDisabled,
    stakeTokenName,
  } = useUnstakeForm(type);

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(amount =>
        onSubmit({ amount: new BigNumber(amount), receiveAmount }),
      )}
    >
      <NumberInputs
        balance={balance}
        control={control as unknown as Control<FieldValues>}
        error={isError}
        helperText={errorMessage}
        inputToken={stakeTokenName}
        inputTooltip={t(keys.inputLabelTooltip, { token: stakeTokenName })}
        label={t(keys.inputTokenLabel, { token: stakeTokenName })}
        name="amount"
        placeholder="0"
        receiveControlName="receiveAmount"
        receiveToken="MAT"
        unit={stakeTokenName}
        variant="filled"
        onMaxClick={onMaxClick}
      />

      <UnstakeTypeSelect
        className={classes.typeSelect}
        setType={setType}
        type={type}
      />

      <Summary className={classes.summary}>
        <SummaryItem
          label={t(keys.exchangeRateLabel)}
          value={t(keys.rateValues, {
            input: t(keys.unit.tokenValue, {
              value: ONE.toFormat(),
              token: stakeTokenName,
            }),
            output: t(keys.unit.tokenValue, {
              value: receiveForOneAmount.decimalPlaces(3).toFormat(),
              token: t(keys.tokens.main),
            }),
          })}
        />
      </Summary>

      <GuardButton
        disabled={isSubmitDisabled}
        loading={isSubmitLoading}
        size="large"
        sx={theme => ({ marginTop: theme.spacing(6) })}
        type="submit"
        variant="contained"
      >
        {t(keys.stake)}
      </GuardButton>
    </form>
  );
}
